'use strict';

// Function to register Alpine.js components
function registerSubscriptionComponents() {
  // Check if Alpine is available
  if (typeof Alpine === 'undefined') {
    console.warn(
      'Alpine.js is not loaded yet. Components will be registered when Alpine is available.'
    );
    return;
  }

  // Swiper slider component - initialize only when visible
  Alpine.data('subscriptionSlider', () => ({
    swiperInstance: null,
    init() {
      // Defer Swiper initialization until element is visible
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.swiperInstance) {
              this.swiperInstance = new Swiper(this.$refs.swiper, {
                autoplay: false,
                speed: 500,
                slidesPerView: 4,
                freeMode: false,
                loop: false,
                watchSlidesProgress: true,
                spaceBetween: 20,
                centeredSlides: true,
                initialSlide: 1,
                navigation: {
                  nextEl: this.$refs.next,
                  prevEl: this.$refs.previous,
                },
                breakpoints: {
                  0: { slidesPerView: 1.15, spaceBetween: 10 },
                  425: { slidesPerView: 1.15, spaceBetween: 10 },
                  550: { slidesPerView: 1.3, spaceBetween: 20 },
                  768: { slidesPerView: 1.7 },
                  1024: { slidesPerView: 2.2 },
                  1280: { slidesPerView: 3, centeredSlides: false },
                  1440: { slidesPerView: 3, centeredSlides: false },
                },
              });
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );

      if (this.$refs.swiper) {
        observer.observe(this.$refs.swiper);
      }
    },
  }));

  // Main subscription component
  Alpine.data('subscriptionPage', () => ({
    selectedCategory: 'unisex',
    isYearly: false,
    cartMap: window.RECHARGE_CART_MAP || {},
    experiences: window.SUBSCRIPTION_EXPERIENCES || {},
    variantPriceCache: {}, // Cache for variant prices
    loadedCategories: new Set(), // Track which categories have been loaded
    swiperInstances: {}, // Track Swiper instances by category

    // Helper to read the Liquid-built map
    _entry(category, key) {
      return (this.cartMap[category] || {})[key] || null;
    },

    _sellingPlanId(category, key, yearly) {
      const e = this._entry(category, key);
      if (!e) return null;
      return yearly ? e.sellingPlanIdYearly || e.sellingPlanIdMonthly : e.sellingPlanIdMonthly;
    },

    isConfigured(experience) {
      const e = this._entry(this.selectedCategory, experience.key);
      if (!e || !e.variantId) return false;
      return !!(e.sellingPlanIdMonthly || e.sellingPlanIdYearly);
    },

    // Money formatting
    formatMoney(cents) {
      if (cents == null) return null;
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.SHOP_MONEY_FORMAT || '${{amount}}');
      }
      try {
        return new Intl.NumberFormat(window.SHOP_LOCALE || 'en-US', {
          style: 'currency',
          currency: window.SHOP_CURRENCY || 'USD',
        }).format(cents / 100);
      } catch (_) {
        return (cents / 100).toFixed(2);
      }
    },

    displayPrice(experience) {
      const e = this._entry(this.selectedCategory, experience.key);
      let cents;
      if (this.isYearly) {
        cents =
          typeof e?.priceYearlyTotalCents === 'number'
            ? e.priceYearlyTotalCents
            : e?.priceYearlyCents;
        if (typeof cents !== 'number' && typeof e?.priceMonthlyCents === 'number')
          cents = e.priceMonthlyCents * 12;
      } else {
        cents = e?.priceMonthlyCents;
      }
      if (typeof cents === 'number') return this.formatMoney(cents);
      const p = this.isYearly ? experience?.prices?.yearly : experience?.prices?.monthly;
      return p || '—';
    },

    // First month discount percentage (30% off = 0.30)
    firstMonthDiscountPercent: 0.3,

    // Display price with first-month discount applied (percentage-based)
    // This matches Shopify's discount calculation: Math.round(price * (1 - discount))
    displayPriceWithDiscount(experience) {
      const e = this._entry(this.selectedCategory, experience.key);

      let cents;
      if (this.isYearly) {
        // For yearly, get the total yearly price
        cents =
          typeof e?.priceYearlyTotalCents === 'number'
            ? e.priceYearlyTotalCents
            : e?.priceYearlyCents;
        if (typeof cents !== 'number' && typeof e?.priceMonthlyCents === 'number') {
          cents = e.priceMonthlyCents * 12;
        }
      } else {
        // For monthly, get the per-month price
        cents = e?.priceMonthlyCents;
      }

      if (typeof cents === 'number') {
        // Apply percentage discount and round UP to nearest cent
        // This ensures customer never pays more than displayed at checkout
        const discountedCents = Math.ceil(cents * (1 - this.firstMonthDiscountPercent));
        return this.formatMoney(discountedCents);
      }

      // Fallback to formatted price string if cents not available
      const p = this.isYearly ? experience?.prices?.yearly : experience?.prices?.monthly;
      if (p && typeof p === 'string') {
        // Try to extract numeric value from formatted price string
        const numericValue = parseFloat(p.replace(/[^0-9.]/g, ''));
        if (!isNaN(numericValue)) {
          const priceCents = Math.round(numericValue * 100);
          const discountedCents = Math.ceil(priceCents * (1 - this.firstMonthDiscountPercent));
          return this.formatMoney(discountedCents);
        }
      }
      return p || '—';
    },

    // Display price with STATIC dollar-based discount (fixed amounts per tier)
    // Discovery: $10 off, Signature: $15 off, Luxury: $25 off
    displayPriceWithStaticDiscount(experience) {
      const e = this._entry(this.selectedCategory, experience.key);

      // Determine discount amount based on tier
      let discountCents = 0;
      if (experience.key === 'discovery') {
        discountCents = 1000; // $10 off
      } else if (experience.key === 'signature') {
        discountCents = 1500; // $15 off
      } else if (experience.key === 'luxury') {
        discountCents = 2500; // $25 off
      }

      // For yearly pricing, multiply discount by 12 (monthly discount * 12 months)
      if (this.isYearly) {
        discountCents = discountCents * 12;
      }

      let cents;
      if (this.isYearly) {
        cents =
          typeof e?.priceYearlyTotalCents === 'number'
            ? e.priceYearlyTotalCents
            : e?.priceYearlyCents;
        if (typeof cents !== 'number' && typeof e?.priceMonthlyCents === 'number')
          cents = e.priceMonthlyCents * 12;
      } else {
        cents = e?.priceMonthlyCents;
      }

      if (typeof cents === 'number') {
        // Apply fixed discount
        const discountedCents = Math.max(0, Math.round(cents - discountCents));
        return this.formatMoney(discountedCents);
      }

      // Fallback to formatted price string if cents not available
      const p = this.isYearly ? experience?.prices?.yearly : experience?.prices?.monthly;
      if (p && typeof p === 'string') {
        // Try to extract numeric value from formatted price string
        const numericValue = parseFloat(p.replace(/[^0-9.]/g, ''));
        if (!isNaN(numericValue)) {
          const priceCents = Math.round(numericValue * 100);
          const discountedCents = Math.max(0, priceCents - discountCents);
          return this.formatMoney(discountedCents);
        }
      }
      return p || '—';
    },

    displayCompare(experience) {
      const e = this._entry(this.selectedCategory, experience.key);
      if (!e) return null;

      if (this.isYearly) {
        const price =
          typeof e.priceYearlyTotalCents === 'number'
            ? e.priceYearlyTotalCents
            : typeof e.priceYearlyCents === 'number'
              ? e.priceYearlyCents * 12
              : null;
        const compare =
          typeof e.baselineYearCents === 'number'
            ? e.baselineYearCents
            : typeof e.compareYearlyCents === 'number'
              ? e.compareYearlyCents
              : null;
        if (typeof price === 'number' && typeof compare === 'number' && compare > price) {
          return this.formatMoney(compare);
        }
        return null;
      } else {
        const price = e?.priceMonthlyCents;
        const compare = e?.compareMonthlyCents;
        if (typeof price === 'number' && typeof compare === 'number' && compare > price) {
          return this.formatMoney(compare);
        }
        return null;
      }
    },

    discountPercent(experience) {
      const e = this._entry(this.selectedCategory, experience.key);
      const price =
        typeof e?.priceYearlyTotalCents === 'number'
          ? e.priceYearlyTotalCents
          : typeof e?.priceYearlyCents === 'number'
            ? e.priceYearlyCents * 12
            : null;
      const compare =
        typeof e?.baselineYearCents === 'number'
          ? e.baselineYearCents
          : typeof e?.compareYearlyCents === 'number'
            ? e.compareYearlyCents
            : null;
      if (typeof price !== 'number' || typeof compare !== 'number' || compare <= price) return null;
      const pct = Math.round(((compare - price) / compare) * 100);
      if (pct <= 0 || !isFinite(pct)) return null;
      return pct + '%';
    },

    // Calculate discount percentage for current billing period (monthly or yearly)
    getDiscountPercent(experience) {
      const e = this._entry(this.selectedCategory, experience.key);
      if (!e) return null;

      let price, compare;

      if (this.isYearly) {
        price =
          typeof e.priceYearlyTotalCents === 'number'
            ? e.priceYearlyTotalCents
            : typeof e.priceYearlyCents === 'number'
              ? e.priceYearlyCents * 12
              : null;
        compare =
          typeof e.baselineYearCents === 'number'
            ? e.baselineYearCents
            : typeof e.compareYearlyCents === 'number'
              ? e.compareYearlyCents
              : null;
      } else {
        price = e?.priceMonthlyCents;
        compare = e?.compareMonthlyCents;
      }

      if (typeof price !== 'number' || typeof compare !== 'number' || compare <= price) return null;
      const pct = Math.round(((compare - price) / compare) * 100);
      if (pct <= 0 || !isFinite(pct)) return null;
      return pct;
    },

    // Add to cart
    async subscribe(experience) {
      const key = experience.key;
      const category = this.selectedCategory;
      const yearly = this.isYearly;

      const entry = this._entry(category, key);
      const planId = this._sellingPlanId(category, key, yearly);

      if (!entry || !entry.variantId || !planId) {
        alert('This subscription option is not configured for this billing choice yet.');
        return;
      }

      if (experience.__adding) return;
      experience.__adding = true;

      try {
        const body = {
          items: [
            {
              id: Number(entry.variantId),
              quantity: 1,
              selling_plan: Number(planId),
              properties: {
                _tier: key,
                _gender: category,
                _billing: yearly ? 'yearly' : 'monthly',
              },
            },
          ],
        };

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify(body),
        });

        if (!res.ok) {
          let msg = 'Add to cart failed';
          try {
            const err = await res.json();
            if (err?.description) msg = err.description;
          } catch (_) {}
          throw new Error(msg);
        }

        if (typeof window.__updateCartAndOpenDrawer === 'function') {
          await window.__updateCartAndOpenDrawer();
        } else {
          window.dispatchEvent(new Event('show-cart'));
        }
      } catch (e) {
        if (window.DEBUG_MODE) console.error(e);
        alert(e.message || 'Could not add to cart.');
      } finally {
        experience.__adding = false;
      }
    },

    // Bundle item processing helpers
    extractProductName(fullTitle) {
      if (!fullTitle) return '';
      return fullTitle.split(' / ')[0].split(' (')[0].trim();
    },

    getItemType(fullTitle) {
      if (!fullTitle) return 'full-bottle';
      const titleLower = fullTitle.toLowerCase();
      if (titleLower.includes('travel size') || titleLower.includes('travel-size')) {
        return 'travel-size';
      } else if (titleLower.includes('sample')) {
        return 'sample';
      }
      return 'full-bottle';
    },

    // Optimized: Batch GraphQL queries for variant prices
    async getVariantPrices(variantIds) {
      if (!variantIds || variantIds.length === 0) return {};

      // Check cache first
      const uncachedIds = variantIds.filter(id => !this.variantPriceCache[String(id)]);
      if (uncachedIds.length === 0) {
        return variantIds.reduce((acc, id) => {
          const cached = this.variantPriceCache[String(id)];
          if (cached) acc[String(id)] = cached;
          return acc;
        }, {});
      }

      // Normalize IDs
      const normalizedIds = uncachedIds
        .map(id => String(id).trim())
        .filter(id => id && !isNaN(Number(id)) && Number(id) > 0);

      if (normalizedIds.length === 0) return {};

      // Convert to GraphQL GIDs
      const gids = normalizedIds.map(id => `gid://shopify/ProductVariant/${id}`);

      const query = `
        query getVariantPrices($ids: [ID!]!) {
          nodes(ids: $ids) {
            ... on ProductVariant {
              id
              price {
                amount
                currencyCode
              }
            }
          }
        }
      `;

      try {
        const res = await fetch('/api/2025-01/graphql.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': '8115ab8e6442c2830f69a0ac86a761e5',
          },
          body: JSON.stringify({ query, variables: { ids: gids } }),
        });

        if (res.ok) {
          const data = await res.json();
          const priceMap = {};

          if (data.data?.nodes) {
            data.data.nodes.forEach((node, index) => {
              if (node && node.id && node.price) {
                const numericId = String(node.id.split('/').pop());
                const priceInCents = Math.round(parseFloat(node.price.amount) * 100);
                priceMap[numericId] = priceInCents;
                // Cache the result
                this.variantPriceCache[numericId] = priceInCents;
              }
            });
          } else if (data.errors && window.DEBUG_MODE) {
            console.error('[GraphQL] Errors:', data.errors);
          }

          // Return all requested prices (cached + new)
          return variantIds.reduce((acc, id) => {
            const idStr = String(id);
            acc[idStr] = priceMap[idStr] || this.variantPriceCache[idStr] || null;
            return acc;
          }, {});
        }
      } catch (e) {
        if (window.DEBUG_MODE) console.warn('Error fetching variant prices via GraphQL:', e);
      }

      return {};
    },

    // Fetch variant by handle and SKU (for bundle items)
    async getVariantByHandleAndSku(handle, sku) {
      try {
        const res = await fetch(`/products/${handle}.js`);
        if (res.ok) {
          const product = await res.json();
          if (product && product.variants) {
            const variant = product.variants.find(v => v.sku === sku);
            if (variant) {
              return {
                variantId: String(variant.id),
                price: variant.price,
              };
            }
          }
        }
      } catch (e) {
        if (window.DEBUG_MODE) console.warn(`[AJAX] Failed to fetch product ${handle}:`, e);
      }
      return null;
    },

    formatPrice(cents) {
      if (cents == null) return 'N/A';
      return this.formatMoney(cents);
    },

    // Optimized: Batch variant lookups and use GraphQL when possible
    async loadBundleItems(experience, bundleItemsData, category = 'unknown') {
      if (!bundleItemsData || !Array.isArray(bundleItemsData)) {
        experience.bundleItems = [];
        return;
      }

      const tier = experience.key || 'unknown';

      // Process bundle items immediately so they render
      experience.bundleItems = bundleItemsData.map(item => ({
        ...item,
        productName: this.extractProductName(item.title),
        type: this.getItemType(item.title),
        formattedPrice: 'Loading...',
        variantId: null,
        priceCents: null,
      }));

      // Collect all handle+SKU pairs for batch processing
      const itemsToFetch = experience.bundleItems.filter(item => item.handle && item.sku);

      // Fetch variant data with rate limiting to avoid 429 errors
      // Process in small batches with delays to respect rate limits
      const batchSize = 2; // Process 2 items at a time
      const delayBetweenBatches = 200; // 200ms delay between batches

      for (let i = 0; i < itemsToFetch.length; i += batchSize) {
        const batch = itemsToFetch.slice(i, i + batchSize);

        // Process batch in parallel
        await Promise.all(
          batch.map(async item => {
            try {
              const variantData = await this.getVariantByHandleAndSku(item.handle, item.sku);
              if (variantData) {
                item.variantId = variantData.variantId;
                item.priceCents = variantData.price;
                item.formattedPrice = this.formatPrice(variantData.price);
              } else {
                item.priceCents = null;
                item.formattedPrice = 'N/A';
              }
            } catch (e) {
              if (window.DEBUG_MODE)
                console.error(`[BUNDLE] Error fetching variant for ${item.handle}:`, e);
              item.priceCents = null;
              item.formattedPrice = 'N/A';
            }
          })
        );

        // Add delay between batches (except for the last batch)
        if (i + batchSize < itemsToFetch.length) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
        }
      }
    },

    getBundleTotal(experience) {
      if (!experience || !experience.bundleItems || !Array.isArray(experience.bundleItems)) {
        return this.formatPrice(599);
      }
      const totalCents = experience.bundleItems.reduce((sum, item) => {
        return sum + (item.priceCents || 0);
      }, 0);
      return this.formatPrice(totalCents + 599);
    },

    getBundleTotalCents(experience) {
      if (!experience || !experience.bundleItems || !Array.isArray(experience.bundleItems)) {
        return 599;
      }
      const totalCents = experience.bundleItems.reduce((sum, item) => {
        return sum + (item.priceCents || 0);
      }, 0);
      return totalCents + 599;
    },

    getSellingPriceCents(experience) {
      const e = this._entry(this.selectedCategory, experience.key);
      if (!e) return null;

      if (this.isYearly) {
        const price =
          typeof e.priceYearlyTotalCents === 'number'
            ? e.priceYearlyTotalCents
            : typeof e.priceYearlyCents === 'number'
              ? e.priceYearlyCents * 12
              : null;
        return price;
      } else {
        return e?.priceMonthlyCents;
      }
    },

    calculateSavingsPercent(experience) {
      const bundleTotalCents = this.getBundleTotalCents(experience);
      const sellingPriceCents = this.getSellingPriceCents(experience);

      if (!sellingPriceCents || typeof sellingPriceCents !== 'number' || sellingPriceCents <= 0) {
        return null;
      }

      if (bundleTotalCents <= sellingPriceCents) {
        return null;
      }

      const savingsCents = bundleTotalCents - sellingPriceCents;
      const savingsPercent = (savingsCents / bundleTotalCents) * 100;

      if (savingsPercent <= 0 || !isFinite(savingsPercent)) {
        return null;
      }

      return Math.round(savingsPercent);
    },

    /* Initialize Swiper for a slider */
    initSwiper(swiperEl, nextBtn, prevBtn, category) {
      if (!swiperEl || !nextBtn || !prevBtn) return;

      // Use provided category or try to detect from parent
      if (!category) {
        const parent = swiperEl.closest('[x-show*="selectedCategory"]');
        if (parent) {
          const xShow = parent.getAttribute('x-show');
          category = xShow?.match(/"(\w+)"/)?.[1];
        }
      }

      if (!category) return;

      // Don't re-initialize if already initialized
      if (this.swiperInstances[category]) return;

      // Defer Swiper initialization until element is visible
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.swiperInstances[category]) {
              this.swiperInstances[category] = new Swiper(swiperEl, {
                autoplay: false,
                speed: 500,
                slidesPerView: 4,
                freeMode: false,
                loop: false,
                watchSlidesProgress: true,
                spaceBetween: 20,
                centeredSlides: true,
                initialSlide: 1,
                navigation: {
                  nextEl: nextBtn,
                  prevEl: prevBtn,
                },
                breakpoints: {
                  0: { slidesPerView: 1.15, spaceBetween: 10 },
                  425: { slidesPerView: 1.15, spaceBetween: 10 },
                  550: { slidesPerView: 1.3, spaceBetween: 20 },
                  768: { slidesPerView: 1.7 },
                  1024: { slidesPerView: 2.2 },
                  1280: { slidesPerView: 3, centeredSlides: false },
                  1440: { slidesPerView: 3, centeredSlides: false },
                },
              });
              observer.disconnect();
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(swiperEl);
    },

    get currentExperiences() {
      return this.experiences[this.selectedCategory] || [];
    },

    async switchCategory(category) {
      this.selectedCategory = category;
      this.isYearly = false;

      // Load bundle items only if not already loaded (non-blocking)
      if (!this.loadedCategories.has(category)) {
        // Don't await - let it load in the background
        this.loadBundleItemsForCategory(category).then(() => {
          this.loadedCategories.add(category);
        });
      }
    },

    // Initialize bundleItems arrays synchronously (non-blocking)
    initBundleItemsSync() {
      Object.keys(this.experiences).forEach(category => {
        this.experiences[category].forEach(exp => {
          if (!exp.bundleItems) {
            exp.bundleItems = [];
          }
        });
      });
    },

    // Load bundle item details asynchronously (non-blocking)
    loadBundleItemsAsync() {
      // Use requestIdleCallback if available for better performance, otherwise use setTimeout
      const scheduleLoad = callback => {
        if (window.requestIdleCallback) {
          requestIdleCallback(callback, { timeout: 2000 });
        } else {
          setTimeout(callback, 100);
        }
      };

      scheduleLoad(() => {
        // Only load bundle items for the currently selected category
        // This runs asynchronously after the page has rendered
        if (!this.loadedCategories.has(this.selectedCategory)) {
          this.loadBundleItemsForCategory(this.selectedCategory).then(() => {
            this.loadedCategories.add(this.selectedCategory);
          });
        }
      });
    },

    // Load bundle items for a specific category - all boxes in parallel
    async loadBundleItemsForCategory(category) {
      const bundleMap = window.BUNDLE_DATA_MAP || {};
      const tiers = ['discovery', 'signature', 'luxury'];

      // Process all tiers in parallel for faster loading
      const loadPromises = tiers.map(async tier => {
        const bundleData = bundleMap[category]?.[tier];
        const experience = this.experiences[category]?.find(exp => exp.key === tier);

        if (experience && !experience.bundleItems) {
          experience.bundleItems = [];
        }

        if (
          experience &&
          bundleData &&
          Array.isArray(bundleData) &&
          bundleData.length > 0 &&
          experience.bundleItems.length === 0
        ) {
          await this.loadBundleItems(experience, bundleData, category);
        } else if (
          experience &&
          (!bundleData || !Array.isArray(bundleData) || bundleData.length === 0)
        ) {
          experience.bundleItems = [];
        }
      });

      // Wait for all boxes to load in parallel
      await Promise.all(loadPromises);
    },
  }));
}

// Register components immediately when Alpine is available
// This script should load before Alpine processes the DOM
(function () {
  // Try to register immediately if Alpine is already loaded
  if (typeof Alpine !== 'undefined') {
    registerSubscriptionComponents();
  }

  // Always listen for alpine:init event (fires even if Alpine is already loaded)
  document.addEventListener(
    'alpine:init',
    function () {
      registerSubscriptionComponents();
    },
    { once: true }
  );

  // Fallback: try again after a short delay in case of timing issues
  setTimeout(function () {
    if (typeof Alpine !== 'undefined' && !Alpine.data('subscriptionPage')) {
      registerSubscriptionComponents();
    }
  }, 100);
})();
